// @ts-ignore
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { BookingStatus } from "../../helpers/enums";
import { removeEmptyFilterQueries, toQuery } from "../../helpers/general";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { useAuth } from "../useAuth";
import { useCookie } from "../useCookie";
import { useToast } from "../useToast";
import { useQueryParam } from "../useQueryParam";
import { useSelectedPlace } from "../place/useSelectedPlace";

const generateBaseUrl = (isAdmin: boolean) => {
  // return isAdmin ? "booking/filter" : "booking/ours";
  return "booking/filter";
};

const generateQueryParams = (isAdmin: boolean, query: string) => {
  const baseEndpoint = generateBaseUrl(isAdmin);
  const endpoint = `${baseEndpoint}?${query}`;
  return endpoint;
};

const mergeFilters = (
  search: string | null,
  bookingStatus: string | null,
  place: string | null,
  filters: any[],
  isPassed: boolean = false,
  specialOffer: string | null,
  partenaire: string | null,
  visiteReference: string | null,
  userId: string | null,
  type: string | null,
  paymentMethod: string | null,
) => {
  const filterQuery: { $and: any[] } = { $and: [] };

  const statusQuery = {
    status: {
      label: {
        $eq: bookingStatus,
      },
    },
  };

  const userQuery = {
    user: {
      fullName: {
        $containsi: search,
      },
      // email: {
      //   $contains: searchQuery
      // }
    },
  };

  const placeQuery = {
    place: {
      id: {
        $eq: place,
      },
    },
  };

  const specialOfferQuery = {
    special: {
      id: {
        $eq: specialOffer,
      },
    },
  };

  const typeQiery = {
    type: {
      $eq: type,
    },
  };

  const paymentMethodQuery = {
    paymentMethod: {
      $eq: paymentMethod,
    },
  };

  const partenaireQuery = {
    user: {
      id: {
        $eq: partenaire,
      },
    },
  };

  const visiteReferenceQuery = {
    visiteReferenceId: {
      $eq: visiteReference,
    },
  };

  if (bookingStatus) {
    filterQuery.$and.push(statusQuery, { isUsed: { $eq: isPassed } });
  }
  if (search) {
    filterQuery.$and.push(userQuery);
  }
  if (place) {
    filterQuery.$and.push(placeQuery);
  }
  if (userId) {
    filterQuery.$and.push({
      user: { id: { $eq: userId } },
    });
  }
  if (specialOffer) {
    filterQuery.$and.push(specialOfferQuery);
  }
  if (partenaire) {
    filterQuery.$and.push(partenaireQuery);
  }
  if (visiteReference) {
    filterQuery.$and.push(visiteReferenceQuery);
  }
  if (filters.length > 0) {
    filterQuery.$and.push(...filters);
  }
  if (type) {
    filterQuery.$and.push(typeQiery);
  }
  if (paymentMethod) {
    filterQuery.$and.push(paymentMethodQuery);
  }
  return filterQuery;
};

export const useBookingQuery = () => {
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [placeId, setPlaceId] = useState<any>("");
  const [filters, setFilters] = useState<any>({});
  const { isAdmin, user } = useAuth();

  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);
  // const [bookingStatus, setBookingStatus] = useState<any>("");

  // const search = useDebounce(searchQuery, 2000);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setCurrentPage(1);
  }, [searchParams, placeId, filters, selectedPlaceId]);

  const isPassed = searchParams.get("status") === BookingStatus.PASSED;
  // const isConfirmed = searchParams.get("status") === BookingStatus.CONFIRMED;
  const { id } = useParams<{ id: string }>();

  const bookingStatus = isPassed
    ? BookingStatus.CONFIRMED
    : searchParams.get("status");
  const search = searchParams.get("search");
  const place = searchParams.get("place");
  const sort = searchParams.get("sort");
  const type = searchParams.get("type");
  const paymentMethod = searchParams.get("paymentMethod");

  // const statusQuery = {
  //   status: {
  //     label: {
  //       $eq: bookingStatus
  //     }
  //   }
  // };
  // const userQuery = {
  //   user: {
  //     fullName: {
  //       $containsi: search
  //     }
  //     // email: {
  //     //   $contains: searchQuery
  //     // }
  //   }
  // };

  const placeQuery = {
    place: {
      id: {
        $eq: place,
      },
    },
  };

  const q = useMemo(() => {
    return {
      placeId: selectedPlaceId,
      filters: mergeFilters(
        search,
        bookingStatus,
        place,
        filters,
        isPassed,
        searchParams.get("specialOffer"),
        searchParams.get("partenaire"),
        searchParams.get("visiteReference"),
        id!,
        type,
        paymentMethod,
      ),
      pagination: {
        page: currentPage,
        limit: 10,
      },
      sort: sort ?? "createdAt:desc",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingStatus,
    currentPage,
    place,
    placeQuery,
    search,
    searchParams.size,
    sort,
    filters,
    mergeFilters,
    selectedPlaceId,
  ]);

  const setBookingFilters = (filters: any) => {
    setFilters(filters);
  };

  // const setSort = (sort: string) => {

  // }

  const endPoint = useMemo(() => {
    const query = removeEmptyFilterQueries(qs.stringify(q, { encode: false }));
    return generateQueryParams(isAdmin, query);
  }, [q, isAdmin]);
  // console.log(endPoint);

  const queryDependencies = [
    "getAllBookings",
    currentPage,
    endPoint,
    selectedPlaceId,
  ];

  const { data, isLoading, isPreviousData } = useQuery(
    queryDependencies,
    () => {
      return apiService.MakeGetRequest(endPoint, token);
    },
    { keepPreviousData: true },
  );
  const deleteBooking = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(`booking/${id}`, token);
  });

  // const bookings: Booking[] = isPassed
  //   ? data?.items.filter((booking: Booking) => booking?.isUsed)
  //   : isConfirmed
  //   ? data?.items.filter((booking: Booking) => !booking?.isUsed)
  //   : data?.items;

  return {
    bookings: data?.items,
    totalItems: data?.meta.totalPages,
    totalCount: data?.meta.totalItems,
    fetchingBookings: isLoading,
    isPreviousData,
    deleteBooking(id: string) {
      deleteBooking.mutate(id, {
        onError(error) {
          toastError(general.fr.message.operationFailed);
        },
        onSuccess() {
          toastSuccess(general.fr.message.bookingDeleted);
          queryClient.invalidateQueries(["getAllBookings", currentPage]);
        },
      });
    },
    mutating: deleteBooking.isLoading,
    currentPage,
    setCurrentPage,
    handleSearch: (query: string) => setSearchQuery(query),
    search: searchQuery,
    placeId,
    setPlaceId,
    setBookingStatus: () => {},
    setFilters: setBookingFilters,
    // setSort: setSort,
  };
};

export const useMultiPlaceAdminPartnerBookingsQuery = (
  placeManagerId: string,
  /**
   * In the format `yyyy-MM`
   * @example "2022-01"
   */
  bookingsDate: string,
) => {
  const { token } = useCookie("vToken");
  const [searchParams] = useSearchParams();
  const { addMultiple } = useQueryParam();

  const currentPage = searchParams?.get("page") ?? "1";

  useEffect(() => {
    addMultiple({ page: "1" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endPoint = `booking/place-manager/${placeManagerId}/bookings/${bookingsDate}/${toQuery(
    {
      page: currentPage,
      limit: 10,
    },
  )}`;

  const queryDependencies = ["getPlaceManagerBookings", endPoint];
  const { data, isLoading } = useQuery(queryDependencies, () => {
    return apiService.MakeGetRequest(endPoint, token);
  });

  return {
    totals: data?.totals as {
      totalPrice: number;
      partnerCommission: number;
      partnerTotal: number;
      adminTotal: number;
    },
    bookings: data?.items as Booking[],
    isLoading,
    totalPages: data?.meta?.totalPages,
    totalItems: data?.meta?.totalItems,
    currentPage,
  };
};

export const useInvoiceBookingsQuery = (invoiceId: string) => {
  const { token } = useCookie("vToken");
  const [searchParams] = useSearchParams();
  const { addMultiple } = useQueryParam();

  const currentPage = searchParams?.get("page") ?? "1";

  useEffect(() => {
    addMultiple({ page: "1" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endPoint = `invoices/${invoiceId}/bookings/${toQuery({
    page: currentPage,
    limit: 10,
  })}`;

  const queryDependencies = ["getInvoiceBookings", endPoint];
  const { data, isLoading } = useQuery(queryDependencies, () => {
    return apiService.MakeGetRequest(endPoint, token);
  });

  return {
    invoice: data?.invoice as MultiPlaceAdminInvoice,
    bookings: data?.items as Booking[],
    isLoading,
    totalPages: data?.meta?.totalPages,
    totalItems: data?.meta?.totalItems,
    currentPage,
  };
};
